<template>
  <div class="inline-search-container">
    <div class="search-box">
      <div class="position-relative">
        <input
          type="text"
          class="form-control search-input"
          v-model="searchQuery"
          @input="onSearchInput"
          @focus="handleFocus"
          @blur="handleBlur"
          @click="handleClick"
          @keydown.enter.prevent="selectFirstResult"
          @keydown.escape.prevent="clearSearch"
          @keydown.down.prevent="navigateResults(1)"
          @keydown.up.prevent="navigateResults(-1)"
          placeholder="Search customers by name, phone, or account number..."
          ref="searchInput"
          autocomplete="off"
          style="cursor: text"
        />
        <i class="mdi mdi-magnify search-icon"></i>

        <!-- Search results dropdown -->
        <div
          v-if="
            isSearchFocused &&
            (searchResults.length > 0 ||
              (searchQuery && searchResults.length === 0))
          "
          class="search-results-dropdown"
        >
          <div v-if="loading" class="search-result-item text-center">
            <b-spinner small variant="primary"></b-spinner>
            <span class="ml-2">Searching...</span>
          </div>

          <div v-else-if="searchResults.length > 0">
            <div
              v-for="(customer, index) in searchResults"
              :key="customer.id"
              class="search-result-item"
              :class="{ selected: index === selectedIndex }"
              @mousedown="selectCustomer(customer)"
              @mouseenter="selectedIndex = index"
            >
              <div class="customer-info">
                <div class="customer-name">{{ customer.displayName }}</div>
                <div class="customer-details">
                  <span class="customer-phone">{{ customer.mobileNo }}</span>
                  <span class="customer-account">{{ customer.accountNo }}</span>
                  <span
                    class="customer-status"
                    :class="{
                      active: customer.active,
                      inactive: !customer.active,
                    }"
                  >
                    {{ customer.active ? "Active" : "Inactive" }}
                  </span>
                </div>
              </div>
              <div class="customer-actions">
                <i class="ri-eye-line text-primary"></i>
              </div>
            </div>
          </div>

          <div
            v-else-if="searchQuery && searchResults.length === 0"
            class="search-result-item text-muted"
          >
            No customers found for "{{ searchQuery }}"
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import errorHandling from "../mixins/errorHandling";

export default {
  name: "InlineCustomerSearch",
  mixins: [errorHandling],
  data() {
    return {
      searchQuery: "",
      searchResults: [],
      loading: false,
      isSearchFocused: false,
      searchTimeout: null,
      selectedIndex: -1,
      customersCache: null,
      cacheTime: null,
      cacheTimeout: 5 * 60 * 1000, // 5 minutes
    };
  },
  mounted() {
    this.loadCustomers();
  },
  methods: {
    async loadCustomers() {
      // Pre-load customers for faster search
      try {
        const response = await axios.get(
          `${
            process.env.VUE_APP_BASE_URL
          }/agent/get-customers/${localStorage.getItem("id")}`
        );

        if (response.status === 200 || response.status === 201) {
          this.customersCache = response.data.data.pageItems;
          this.cacheTime = Date.now();
        }
      } catch (error) {
        // Silent fail - search will work without cache
      }
    },

    onSearchInput() {
      // Reset selected index when search changes
      this.selectedIndex = -1;

      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // If search query is empty, clear results
      if (!this.searchQuery.trim()) {
        this.searchResults = [];
        return;
      }

      // Set a timeout to avoid too many API calls
      this.searchTimeout = setTimeout(() => {
        this.searchCustomers();
      }, 300);
    },

    async searchCustomers() {
      if (!this.searchQuery.trim()) return;

      this.loading = true;
      try {
        // Check cache first
        const now = Date.now();
        if (
          this.customersCache &&
          this.cacheTime &&
          now - this.cacheTime < this.cacheTimeout
        ) {
          this.filterCustomers(this.customersCache);
          this.loading = false;
          return;
        }

        const response = await axios.get(
          `${
            process.env.VUE_APP_BASE_URL
          }/agent/get-customers/${localStorage.getItem("id")}`
        );

        if (response.status === 200 || response.status === 201) {
          const customers = response.data.data.pageItems;

          // Cache the results
          this.customersCache = customers;
          this.cacheTime = now;

          this.filterCustomers(customers);
        } else {
          this.throwError();
        }
      } catch (error) {
        this.handleError(
          error.response?.data?.message || "Error searching customers"
        );
        this.searchResults = [];
      } finally {
        this.loading = false;
      }
    },

    filterCustomers(customers) {
      // Filter customers based on search query
      const searchQueryLower = this.searchQuery.toLowerCase();
      this.searchResults = customers
        .filter(
          (customer) =>
            customer.displayName.toLowerCase().includes(searchQueryLower) ||
            customer.mobileNo.includes(this.searchQuery) ||
            customer.accountNo.includes(this.searchQuery)
        )
        .slice(0, 10); // Limit to 10 results
    },

    selectCustomer(customer) {
      // Navigate directly to the verify payment page with force reload
      const targetRoute = `/agent/verify-payment/${customer.id}`;

      // If we're already on the verify payment page, force reload
      if (this.$route.path.includes("/agent/verify-payment/")) {
        // Use window.location to force a full page reload
        window.location.href =
          targetRoute + "?from=inline-search&t=" + Date.now();
      } else {
        // Normal navigation for other pages
        this.$router.push({
          path: targetRoute,
          query: { from: "inline-search", t: Date.now() },
        });
      }

      // Clear search
      this.searchQuery = "";
      this.searchResults = [];
      this.isSearchFocused = false;
      this.selectedIndex = -1;
    },

    handleClick() {
      this.isSearchFocused = true;
    },

    handleFocus() {
      this.isSearchFocused = true;
    },

    handleBlur() {
      // Delay hiding results to allow for click events
      setTimeout(() => {
        this.isSearchFocused = false;
        this.selectedIndex = -1;
      }, 200);
    },

    selectFirstResult() {
      if (this.searchResults.length > 0) {
        const customer =
          this.selectedIndex >= 0
            ? this.searchResults[this.selectedIndex]
            : this.searchResults[0];
        this.selectCustomer(customer);
      }
    },

    clearSearch() {
      this.searchQuery = "";
      this.searchResults = [];
      this.isSearchFocused = false;
      this.selectedIndex = -1;
      this.$refs.searchInput.blur();
    },

    navigateResults(direction) {
      if (this.searchResults.length === 0) return;

      this.selectedIndex += direction;

      if (this.selectedIndex < 0) {
        this.selectedIndex = this.searchResults.length - 1;
      } else if (this.selectedIndex >= this.searchResults.length) {
        this.selectedIndex = 0;
      }
    },

    focusSearch() {
      this.$nextTick(() => {
        this.$refs.searchInput.focus();
        this.isSearchFocused = true;
      });
    },
  },

  beforeDestroy() {
    // Clean up timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
};
</script>

<style scoped>
.inline-search-container {
  position: relative;
  max-width: 600px;
  margin: 0 auto;
}

.search-box {
  position: relative;
}

.search-box .form-control,
.search-box .search-input {
  padding-right: 40px;
  border-radius: 25px;
  border: 2px solid #e3f2fd;
  transition:
    border-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out;
  position: relative;
  z-index: 1;
  cursor: text !important;
}

.search-box .form-control:focus,
.search-box .search-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  outline: none;
}

.search-icon {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  font-size: 1.1rem;
  pointer-events: none;
  z-index: 2;
}

.search-results-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  max-height: 400px;
  overflow-y: auto;
  z-index: 1000;
  margin-top: 5px;
}

.search-result-item {
  padding: 15px 20px;
  cursor: pointer;
  border-bottom: 1px solid #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background-color 0.15s ease-in-out;
}

.search-result-item:hover,
.search-result-item.selected {
  background-color: #f8f9fa;
}

.search-result-item.selected {
  background-color: #e3f2fd;
  border-left: 4px solid #007bff;
}

.search-result-item:last-child {
  border-bottom: none;
}

.customer-info {
  flex: 1;
}

.customer-name {
  font-weight: 600;
  color: #495057;
  margin-bottom: 5px;
  font-size: 1rem;
}

.customer-details {
  display: flex;
  gap: 15px;
  font-size: 0.875rem;
  color: #6c757d;
}

.customer-phone::before {
  content: "📱 ";
  margin-right: 4px;
}

.customer-account::before {
  content: "#";
  margin-right: 2px;
}

.customer-status {
  font-size: 0.75rem;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 500;
}

.customer-status.active {
  background-color: #d4edda;
  color: #155724;
}

.customer-status.inactive {
  background-color: #f8d7da;
  color: #721c24;
}

.customer-actions {
  color: #007bff;
  font-size: 1.2rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .inline-search-container {
    max-width: 100%;
    padding: 0 15px;
  }

  .customer-details {
    flex-direction: column;
    gap: 5px;
  }
}
</style>
